import { useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useCalendarDataContext } from '../contexts/CalendarDataContext';
import { useTranslation } from '../i18n';
import useTooltip from '../hooks/useTooltip';
import Tooltip from './Tooltip';
import './SyncIndicator.scss';

/**
 * SyncIndicator - shows sync status for authenticated users
 * Reflects actual backend connectivity and data sync state
 * 
 * States:
 * - connected: Successfully synced with backend
 * - offline: Using cached data (backend unavailable or errors)
 * - error: Backend unavailable and no cached data
 * - connecting: Initial connection in progress
 */
function SyncIndicator() {
  const { isAuthenticated } = useAuth();
  const { error, isLoading } = useCalendarDataContext();
  const { t } = useTranslation();

  // Derive status from props instead of using state
  const status = useMemo(() => {
    if (!isAuthenticated) {
      return 'connecting';
    }
    
    // Determine status based on actual data state
    if (isLoading) {
      return 'connecting';
    } else if (error === 'offline') {
      // Using cached data (backend unavailable)
      return 'offline';
    } else if (error) {
      // Error with no cached data
      return 'error';
    } else {
      // Successfully synced
      return 'connected';
    }
  }, [isAuthenticated, error, isLoading]);

  const statusClasses = {
    connected: 'sync-indicator--connected',
    connecting: 'sync-indicator--connecting',
    offline: 'sync-indicator--offline',
    error: 'sync-indicator--error',
  };

  const statusLabels = {
    connected: t('sync.connected'),
    connecting: t('sync.connecting'),
    offline: t('sync.offline'),
    error: t('sync.error'),
  };

  const { tooltipProps, handleMouseEnter, handleMouseLeave } = useTooltip({
    title: statusLabels[status],
    placement: 'bottom'
  });

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div 
      className={`sync-indicator ${statusClasses[status]}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span className="sync-indicator__dot" />
      <Tooltip {...tooltipProps} />
    </div>
  );
}

export default SyncIndicator;
